import { Suspense, lazy, useCallback, useEffect, useMemo, useState } from 'react';
import { css } from '@emotion/react';
import dayjs from 'dayjs';
import type { JsonData } from 'json-edit-react';
import { preferenceActions, usePreferenceStore } from '@frontend/user-preferences';
import { theme } from '@frontend/theme';
import {
  DropdownField,
  InlineNotification,
  Text,
  TextButton,
  useFormField,
  useAlert,
  Button,
  SearchField,
} from '@frontend/design-system';

const clonePreferenceValue = <T,>(value: T) => {
  try {
    return structuredClone<T>(value);
  } catch {
    console.error('Structured clone failed, falling back to JSON clone');
    return;
  }
};

const safeStringify = (value: JsonData | null | undefined): string => {
  try {
    const result = JSON.stringify(value);
    return typeof result === 'string' ? result : 'undefined';
  } catch {
    return '';
  }
};

const LazyJsonEditor = lazy(async () => {
  const module = await import('json-edit-react');
  return { default: module.JsonEditor ?? module.default };
});

async function copyTextToClipboard(text: string) {
  if ('clipboard' in navigator) {
    return await navigator.clipboard.writeText(text);
  } else {
    return document.execCommand('copy', true, text);
  }
}

export const PreferencesSection = () => {
  const alerts = useAlert();
  const { prefs } = usePreferenceStore((state) => ({ prefs: state.prefs }));
  const [initialValueString, setInitialValueString] = useState('undefined');
  const [initialData, setInitialData] = useState<JsonData | null>(null);
  const [editorData, setEditorData] = useState<JsonData | null>(null);
  const [lastUpdated, setLastUpdated] = useState<string | null>(null);

  const preferenceKeys = useMemo(() => Object.keys(prefs).sort(), [prefs]);
  const dropdownFieldProps = useFormField({ type: 'dropdown', value: preferenceKeys[0] ?? '' });
  const searchFilter = useFormField({ type: 'text' });
  const selectedKey = dropdownFieldProps.value;
  const filteredKeys = preferenceKeys;

  useEffect(() => {
    if (!selectedKey) {
      setInitialData(null);
      setEditorData(null);
      setInitialValueString('undefined');
      setLastUpdated(null);
      return;
    }

    const preference = prefs[selectedKey];
    if (!preference) {
      setInitialData(null);
      setEditorData(null);
      setInitialValueString('undefined');
      setLastUpdated(null);
      return;
    }

    const clonedValue = clonePreferenceValue(preference.value ?? null);
    const nextString = safeStringify(clonedValue);

    setInitialData(clonedValue);
    setEditorData(clonedValue);
    setInitialValueString(nextString);

    setLastUpdated(preference.updatedAt ?? null);
  }, [prefs, selectedKey, initialValueString]);

  const handleEditorDataChange = useCallback((data: JsonData) => {
    setEditorData(data);
  }, []);

  const hasChanges = useMemo(() => safeStringify(editorData) !== initialValueString, [editorData, initialValueString]);

  const handleReset = useCallback(() => {
    setEditorData(initialData === null ? null : clonePreferenceValue(initialData));
  }, [initialData]);

  const handleApply = useCallback(async () => {
    if (!selectedKey) {
      alerts.error('Select a preference to update.');
      return;
    }

    const normalizedData = editorData ?? null;

    const updatedAt = new Date().toISOString();
    try {
      preferenceActions.setPreference(selectedKey as any, normalizedData, updatedAt);
      setInitialData(normalizedData);
      setEditorData(normalizedData);
      const nextString = safeStringify(normalizedData);
      setInitialValueString(nextString);
      setLastUpdated(updatedAt);
      alerts.success('Preference updated.');
    } catch (error) {
      console.error('Failed to update preference', error);
      alerts.error('Failed to update preference.');
    }
  }, [alerts, editorData, selectedKey]);

  const handleCopyJson = useCallback(async () => {
    try {
      await copyTextToClipboard(JSON.stringify(editorData ?? null, null, 2));
      alerts.success('Copied JSON to clipboard.');
    } catch (error) {
      console.error('Failed to copy preference JSON', error);
      alerts.error('Failed to copy preference JSON.');
    }
  }, [alerts, editorData]);

  const isApplyDisabled = !selectedKey || !hasChanges;
  const noResults = filteredKeys.length === 0;

  return (
    <div
      css={css`
        display: flex;
        flex-direction: column;
        gap: ${theme.spacing(1)};
        height: 100%;
      `}
    >
      <div
        css={css`
          display: flex;
          padding: ${theme.spacing(2)};
          padding-bottom: ${theme.spacing(1)};
          border-bottom: 1px solid ${theme.colors.neutral10};
          flex-wrap: wrap;
          gap: ${theme.spacing(1)};
        `}
      >
        <DropdownField
          label='Preference'
          name='preference-select'
          {...dropdownFieldProps}
          placeholder='Select a preference'
          disabled={noResults}
          css={css`
            min-width: 280px;
            max-width: 280px;
          `}
        >
          {filteredKeys.map((key) => (
            <DropdownField.Option className='user-preferences-devtools-dropdown' key={key} value={key}>
              {key}
            </DropdownField.Option>
          ))}
        </DropdownField>
        <TextButton onClick={handleCopyJson} disabled={!selectedKey}>
          Copy JSON
        </TextButton>
        <SearchField
          css={css`
            min-width: 280px;
            max-width: 280px;
          `}
          placeholder='Filter JSON...'
          {...searchFilter}
          name='search'
        />
      </div>
      {noResults && (
        <InlineNotification status='info'>Try updating your search to locate a preference.</InlineNotification>
      )}

      {!noResults && (
        <>
          <div
            css={css`
              display: flex;
              justify-content: space-between;
              align-items: center;
              padding: ${theme.spacing(0, 2)};
            `}
          >
            <Text size='small' color='subdued'>
              {selectedKey ? `Editing: ${selectedKey}` : 'Select a preference to view its JSON.'}
            </Text>
          </div>
          <div
            css={css`
              background: ${theme.colors.white};
              flex: 1;
              padding: ${theme.spacing(1, 1)};
            `}
          >
            <Suspense
              fallback={
                <Text size='small' color='subdued'>
                  Loading JSON editor…
                </Text>
              }
            >
              <LazyJsonEditor
                rootName={selectedKey || 'preference'}
                searchText={searchFilter.value !== '' ? searchFilter.value : undefined}
                searchFilter='all'
                data={editorData ?? null}
                setData={handleEditorDataChange}
                enableClipboard
                showCollectionCount
                collapse={false}
                css={css`
                  background: ${theme.colors.neutral5} !important;
                `}
                indent={2}
              />
            </Suspense>
          </div>
          <div
            css={css`
              position: sticky;
              bottom: 0;
              z-index: ${theme.zIndex.alerts};
              background: ${theme.colors.white};
              padding: ${theme.spacing(1)};
              border-top: 1px solid ${theme.colors.neutral20};
              display: flex;
              gap: ${theme.spacing(1)};
              justify-content: flex-end;
              align-items: center;
            `}
          >
            {lastUpdated && (
              <Text
                size='small'
                css={css`
                  margin-right: auto;
                `}
                color='subdued'
              >
                Last updated {dayjs(lastUpdated).format('MMM D, YYYY h:mm:ss A')}
              </Text>
            )}
            <Button variant='secondary' onClick={handleReset} disabled={!hasChanges}>
              Reset
            </Button>
            <Button onClick={handleApply} disabled={isApplyDisabled}>
              Apply
            </Button>
          </div>
        </>
      )}
    </div>
  );
};
